import { cn } from '@nextui-org/react'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useCompactViewport } from '~/hooks/useCompactViewport'
import { Typography } from '../common/Typography'

const MODAL_OVERLAY_ATTR = 'data-modal-overlay'

export const useModal = () => {
  const [isOpen, setIsOpen] = useState(false)
  const open = useCallback(() => setIsOpen(true), [])
  const close = useCallback(() => setIsOpen(false), [])
  const toggle = useCallback(() => setIsOpen(v => !v), [])
  return { isOpen, open, close, toggle }
}

export const SettingsIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    width='24'
    height='24'
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M12 2V4'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M12 20V22'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M22 12H20'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M4 12H2'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
)

export const CloseIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    width='24'
    height='24'
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M18 6L6 18'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M6 6L18 18'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
)

const CloseButton = ({ onClick }: { onClick: () => void }) => (
  <button
    onClick={onClick}
    className='absolute top-4 right-4 rounded-full text-neutral-icon-default hover:bg-neutral-fill-fill-4 hover:text-neutral-icon-hover transition-colors'
    aria-label='Close modal'
  >
    <CloseIcon className='size-6' />
  </button>
)

export const ModalBody = ({
  children,
  className
}: {
  children: React.ReactNode
  className?: string
}) => {
  const isCompactViewport = useCompactViewport()
  return (
    <div className={cn(isCompactViewport ? 'px-4 py-4' : 'px-[48px] py-[24px]', className)}>
      {children}
    </div>
  )
}

export const ModalFooter = ({
  children,
  className,
  startContent,
  endContent
}: {
  children?: React.ReactNode
  className?: string
  startContent?: React.ReactNode
  endContent?: React.ReactNode
}) => (
  <div
    className={cn(
      'py-[16px] px-[32px] flex items-center bg-tokens-neutral-bg-grey-1 border-t border-tokens-neutral-border-2',
      startContent ? 'justify-between' : 'justify-end',
      className
    )}
  >
    {startContent && <div className='flex items-center gap-2'>{startContent}</div>}
    {(endContent || children) && (
      <div className='flex items-center gap-2'>{endContent || children}</div>
    )}
  </div>
)

export const ModalHeaderContent: React.FC<{
  icon?: React.ReactNode
  title?: React.ReactNode
  description?: React.ReactNode
  className?: string
  children?: React.ReactNode
}> = ({ icon, title, description, className, children }) => (
  <div className={cn('flex items-center gap-4 flex-col', className)}>
    {icon && (
      <div className='bg-[#846FE1] bg-opacity-10 flex items-center justify-center size-10 rounded-full text-[#2E1F36] text-opacity-90 select-none'>
        {icon}
      </div>
    )}
    <div className='flex flex-col'>
      {title && (
        <Typography fontFamily='geist' fontSize='titleLg' fontWeight='bold' className='select-none'>
          {title}
        </Typography>
      )}
      {description && (
        <Typography
          fontFamily='geist'
          fontSize='labelSm'
          className='text-neutral-text-secondary select-none'
        >
          {description}
        </Typography>
      )}
      {children}
    </div>
  </div>
)

export interface ModalProps {
  isOpen: boolean
  onClose: () => void
  children: React.ReactNode
  className?: string
  overlayClassName?: string
  hideCloseButton?: boolean
  disableOutsideClick?: boolean
  /** When true, pressing Escape will not close the modal */
  disableEscKey?: boolean
  /** When true, renders children directly without the scrollable wrapper */
  rawContent?: boolean
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  children,
  className,
  overlayClassName,
  hideCloseButton = true,
  disableOutsideClick = false,
  disableEscKey = false,
  rawContent = false
}) => {
  const isCompactViewport = useCompactViewport()
  const overlayRef = useRef<HTMLDivElement>(null)
  const modalRef = useRef<HTMLDivElement>(null)
  const previouslyFocusedElement = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (!isOpen) return

    // Store the previous overflow value
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      // Only restore if this modal was the one that set it
      document.body.style.overflow = previousOverflow
    }
  }, [isOpen])

  useEffect(() => {
    if (isOpen) {
      previouslyFocusedElement.current = document.activeElement as HTMLElement
      if (modalRef.current) {
        modalRef.current.focus()
      }
    } else {
      if (previouslyFocusedElement.current) {
        previouslyFocusedElement.current.focus()
      }
    }
  }, [isOpen])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isOpen) return
      if (event.key === 'Escape' && !disableEscKey) {
        const allOverlays = document.querySelectorAll(`[${MODAL_OVERLAY_ATTR}]`)
        const topmost = allOverlays[allOverlays.length - 1]
        if (topmost === overlayRef.current) {
          onClose()
        }
        return
      }
      if (event.key === 'Tab') {
        const focusableElements = modalRef.current?.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
        if (!focusableElements || focusableElements.length === 0) return
        const firstElement = focusableElements[0] as HTMLElement
        const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement
        if (event.shiftKey && document.activeElement === firstElement) {
          event.preventDefault()
          lastElement.focus()
        } else if (!event.shiftKey && document.activeElement === lastElement) {
          event.preventDefault()
          firstElement.focus()
        }
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose, disableEscKey])

  const handleOverlayClick = (event: React.MouseEvent) => {
    if (disableOutsideClick) return
    if (event.target === event.currentTarget) {
      onClose()
    }
  }

  const sizeClass = isCompactViewport
    ? 'w-[600px] max-w-[calc(100vw-24px)]'
    : 'w-[600px] max-w-full'

  if (!isOpen) return null

  return createPortal(
    <div
      ref={overlayRef}
      {...{ [MODAL_OVERLAY_ATTR]: '' }}
      className={cn('fixed inset-0 z-50 overflow-y-auto', 'bg-[rgba(0,0,0,0.45)]')}
    >
      <div
        className={cn(
          'min-h-full flex justify-center items-start',
          isCompactViewport ? 'py-4' : 'py-[60px]',
          overlayClassName
        )}
        onClick={handleOverlayClick}
      >
        <div
          ref={modalRef}
          className={cn(
            'relative bg-white rounded-[28px] shadow-popover overflow-clip',
            'transform transition-all duration-200 animate-enter',
            'w-full flex flex-col',
            isCompactViewport && 'max-h-[calc(100vh-32px)] overflow-y-auto',
            sizeClass,
            className
          )}
          role='dialog'
          aria-modal='true'
          tabIndex={-1}
        >
          {!hideCloseButton && <CloseButton onClick={onClose} />}
          {rawContent ? children : <div className='flex-1 min-h-0 overflow-y-auto'>{children}</div>}
        </div>
      </div>
    </div>,
    document.body
  )
}
